/** Tables wider than this (ch) use full-width + horizontal scroll; narrower tables fit content. */
export const COMPACT_TABLE_MAX_CH = 136;

/** Relative column weights (converted to % of full table width). */
export const COLUMN_SIZE_WEIGHT = {
  xs: 0.45,
  sm: 0.65,
  md: 1,
  lg: 1.35,
  xl: 1.85,
  /** Fallback only — prefer resolveActionsLayoutWeight(column) */
  actions: 0.72,
};

/** ~ch per character at xs uppercase headers — column must fit title on one line. */
const HEADER_CHAR_WEIGHT = 0.062;

const compactRe =
  /type|code|poste|capacit|qty|quantit|nombre|contrôle|controle|ok|urgent|matricule|^\s*n\s*$|récup|recup/i;

const wideRe =
  /observation|note|comment|description|adresse|address|email|contact|entreprise|company|client|site|emplacement|location|sujet|message|nom|name|série|serie|serial|extincteur|inventaire|structure|affectation|détaill|detai|accessoire/i;

export function isActionsColumnMeta(column) {
  return (
    column?.sticky === true ||
    column?.accessor === 'actions' ||
    column?.header === 'Actions' ||
    column?.size === 'actions'
  );
}

export function resolveActionButtonCount(column) {
  if (typeof column?.actionCount === 'number' && column.actionCount > 0) {
    return column.actionCount;
  }
  return 3;
}

/** ~3.4ch per icon button + padding */
export function resolveActionMinCh(column) {
  if (typeof column?.actionMinCh === 'number' && column.actionMinCh > 0) {
    return column.actionMinCh;
  }
  const count = resolveActionButtonCount(column);
  return Math.ceil(count * 3.4 + 3);
}

/** Share of table width (not px) — enough for icons without a huge empty gutter. */
export function resolveActionsLayoutWeight(column) {
  const n = resolveActionButtonCount(column);
  return 0.42 + n * 0.11;
}

export function headerLabelWeight(label) {
  const text = typeof label === 'string' ? label.trim() : '';
  const len = text.length || 4;
  return len * HEADER_CHAR_WEIGHT;
}

export function resolveColumnWeight(column) {
  if (column?.width) return null;
  if (typeof column?.weight === 'number' && column.weight > 0) return column.weight;
  if (column?.size && COLUMN_SIZE_WEIGHT[column.size] != null) {
    return COLUMN_SIZE_WEIGHT[column.size];
  }
  if (isActionsColumnMeta(column)) return resolveActionsLayoutWeight(column);

  const label = `${column?.accessor ?? ''}`.toLowerCase();
  const header = `${column?.header ?? ''}`.toLowerCase();

  if (compactRe.test(label) || compactRe.test(header)) return COLUMN_SIZE_WEIGHT.sm;
  if (wideRe.test(label) || wideRe.test(header)) return COLUMN_SIZE_WEIGHT.lg;

  return COLUMN_SIZE_WEIGHT.md;
}

function mergeContentAndHeaderWeight(contentWeight, headerText) {
  const headerW = headerLabelWeight(headerText);
  return Math.max(contentWeight, headerW) + headerW * 0.15;
}

function resolveMergedLayoutWeight(column, contentWeight, header, isActions) {
  if (column?.compactLayout) {
    const headerW = headerLabelWeight(header);
    return Math.max(contentWeight, headerW * 1.05);
  }
  return mergeContentAndHeaderWeight(contentWeight, header);
}

/** Minimum column width (ch) so headers and cell content stay inside the column. */
export function columnMinCh(contentWeight, headerText, isActions = false, column = null) {
  if (isActions) return resolveActionMinCh(column);

  const text = typeof headerText === 'string' ? headerText.trim() : '';
  if (/^(état|etat|statut|status)$/i.test(text)) return 12;
  if (/date/i.test(text)) return 13;
  const len = text.length || 4;
  const headerMin = len * 0.72 + 2;
  const contentMin = Math.max(4, (contentWeight || COLUMN_SIZE_WEIGHT.md) * 5.5);
  let minCh = Math.max(headerMin, contentMin);
  if (contentWeight <= COLUMN_SIZE_WEIGHT.sm) {
    minCh = Math.min(minCh, Math.max(contentMin, headerMin * 0.85));
  }
  return Math.ceil(minCh * 10) / 10;
}

function layoutFromMergedRows(rows) {
  const minWidthsCh = rows.map((r) => r.minCh);
  const tableMinWidthCh =
    Math.ceil(minWidthsCh.reduce((sum, w) => sum + w, 0) * 10) / 10;

  const merged = rows.map((r) => Math.max(r.merged, r.minCh * 0.065));
  let total = merged.reduce((sum, w) => sum + w, 0);

  let percents = merged.map((m) => (m / total) * 100);
  if (tableMinWidthCh > 0) {
    percents = percents.map((pct, i) =>
      Math.max(pct, (minWidthsCh[i] / tableMinWidthCh) * 100)
    );
    const pctSum = percents.reduce((sum, p) => sum + p, 0);
    percents = percents.map((p) => (p / pctSum) * 100);
  }

  return {
    widths: percents.map((p) => `${p.toFixed(4)}%`),
    minWidthsCh,
    tableMinWidthCh,
  };
}

/** Returns % col widths + per-column min (ch) + total min table width. */
export function getTableLayoutFromColumns(columns) {
  if (!columns?.length) return { widths: [], minWidthsCh: [], tableMinWidthCh: 0 };

  if (columns.every((c) => c.width)) {
    return {
      widths: columns.map((c) => c.width),
      minWidthsCh: columns.map((c) => (isActionsColumnMeta(c) ? resolveActionMinCh(c) : 8)),
      tableMinWidthCh: columns.length * 8,
    };
  }

  if (columns.some((c) => c.width)) {
    return {
      widths: columns.map((c) => c.width ?? `${COLUMN_SIZE_WEIGHT.md}%`),
      minWidthsCh: columns.map((c) => (isActionsColumnMeta(c) ? resolveActionMinCh(c) : 8)),
      tableMinWidthCh: columns.length * 8,
    };
  }

  const rows = columns.map((col) => {
    const contentW = resolveColumnWeight(col);
    const header = typeof col.header === 'string' ? col.header : '';
    const isActions = isActionsColumnMeta(col);
    return {
      merged: resolveMergedLayoutWeight(col, contentW, header, isActions),
      minCh: columnMinCh(contentW, header, isActions, col),
    };
  });

  return layoutFromMergedRows(rows);
}

export function getTableLayoutFromWeights(weightList, headerLabels = null, columnMetas = null) {
  if (!weightList?.length) return { widths: [], minWidthsCh: [], tableMinWidthCh: 0 };

  const rows = weightList.map((w, i) => {
    const header = headerLabels?.[i] ?? '';
    const meta = columnMetas?.[i] ?? null;
    const isActions = meta ? isActionsColumnMeta(meta) : /^actions$/i.test(header.trim());
    const colMeta = isActions ? meta || { header: 'Actions' } : meta;
    const weight = isActions && !meta?.weight ? resolveActionsLayoutWeight(colMeta) : w;
    const merged = colMeta?.compactLayout
      ? weight
      : mergeContentAndHeaderWeight(weight, header);
    return {
      merged,
      minCh: columnMinCh(weight, header, isActions, colMeta),
    };
  });

  return layoutFromMergedRows(rows);
}

/** Table width: fit content for small tables, 100% + minWidth for wide grids. */
export function resolveTableContainerStyle(tableMinWidthCh, layout = 'auto') {
  if (!tableMinWidthCh || tableMinWidthCh <= 0) {
    return { style: { width: '100%' }, compact: false };
  }
  const compact =
    layout === 'compact' ||
    (layout === 'auto' && tableMinWidthCh <= COMPACT_TABLE_MAX_CH);
  if (compact) {
    return {
      compact: false,
      style: { width: '100%', minWidth: `${tableMinWidthCh}ch` },
    };
  }
  return {
    compact: false,
    style: { width: '100%', minWidth: `${tableMinWidthCh}ch` },
  };
}

/** Returns CSS width strings that sum to 100%. */
export function computeColumnWidths(columns) {
  return getTableLayoutFromColumns(columns).widths;
}

export function computeWidthsFromWeights(weightList, headerLabels = null) {
  return getTableLayoutFromWeights(weightList, headerLabels).widths;
}
